import { socket } from '@/socket';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { FlowChangedNotification } from '@/types/flows/flow-changed.notification';
import type { MediaPlaylistUpdatedNotification } from '@/types/flows/media-playlist-updated.notification';
import type { FlowMediaSettingsUpdatedNotification } from '@/types/flows/flow-media-settings-updated.notification';
import type { ContributorUpdatedNotification } from '@/types/flows/contributor-updated.notification';
import type { MediaAssetApprovalUpdatedNotification } from '@/types/media/media-asset-approval-updated.notification';
import type { QueryClient } from '@tanstack/react-query';
import { patchContributorEverywhere } from '@/state/mutations/contributors/patch-contributor-cache';
import { useEffect, useRef, useState } from 'react';
import { useSocketStore } from '@/state/stores/use-socket-store';
import { useAuth } from '@clerk/clerk-react';
import { useEventStore } from '@/state/stores/use-event-store';

class FlowSubscriptionManager {
  private subscriptions = new Map<string, Set<FlowSubscriptionHandler>>();
  private globalHandlersSetup = false;

  subscribe(flowId: string, handler: FlowSubscriptionHandler) {
    if (!this.subscriptions.has(flowId)) {
      this.subscriptions.set(flowId, new Set());
    }
    this.subscriptions.get(flowId)!.add(handler);
    this.setupGlobalHandlers();
  }

  unsubscribe(flowId: string, handler: FlowSubscriptionHandler) {
    const handlers = this.subscriptions.get(flowId);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.subscriptions.delete(flowId);
      }
    }
  }

  // Public method to ensure global handlers are set up
  ensureGlobalHandlers() {
    this.setupGlobalHandlers();
  }

  private setupGlobalHandlers() {
    if (this.globalHandlersSetup) return;
    this.globalHandlersSetup = true;

    socket.on('flowChanged', (notification: FlowChangedNotification) => {
      const keys = [notification.flowDocumentId, notification.originId].filter(Boolean);
      this.subscriptions.forEach((handlers, subscribedFlowId) => {
        if (!keys.includes(subscribedFlowId)) return;
        handlers.forEach((handler) => handler.onFlowChanged?.(notification));
      });
    });

    socket.on('mediaPlaylistUpdated', (notification: MediaPlaylistUpdatedNotification) => {
      const keys = [notification.flowDocumentId, notification.originId].filter(Boolean);
      this.subscriptions.forEach((handlers, subscribedFlowId) => {
        if (!keys.includes(subscribedFlowId)) return;
        handlers.forEach((handler) => handler.onMediaPlaylistUpdated?.(notification));
      });
    });

    socket.on('flowMediaSettingsUpdated', (notification: FlowMediaSettingsUpdatedNotification) => {
      const keys = [notification.flowDocumentId, notification.originId].filter(Boolean);
      this.subscriptions.forEach((handlers, subscribedFlowId) => {
        if (!keys.includes(subscribedFlowId)) return;
        handlers.forEach((handler) => handler.onFlowMediaSettingsUpdated?.(notification));
      });
    });

    socket.on('contributorUpdated', (notification: ContributorUpdatedNotification) => {
      patchContributorFromNotification(notification);
    });

    socket.on('mediaAssetApprovalUpdated', (notification: MediaAssetApprovalUpdatedNotification) => {
      const keys = [notification.flowDocumentId, notification.originId].filter(Boolean);
      this.subscriptions.forEach((handlers, subscribedFlowId) => {
        // Flow-room emit includes origin ids; media-parent-only emits still reach media.list sync.
        if (keys.length > 0 && !keys.includes(subscribedFlowId)) return;
        if (keys.length === 0) return;
        handlers.forEach((handler) => handler.onMediaAssetApprovalUpdated?.(notification));
      });
    });

    socket.on('flowUpdated', (flow: FlowDto) => {
      // Update eventFlows globally whenever any flow updates
      // Uses version comparison to avoid race conditions - only updates if new data is fresher
      const eventFlows = useEventStore.getState().eventFlows;
      if (eventFlows) {
        const updatedFlows = eventFlows.map((f) => {
          const isMatch =
            f.id === flow.id || f.origin === flow.id || (typeof f.origin === 'object' && f.origin?.id === flow.id);
          if (!isMatch) return f;

          // Only update if the new flow has a higher version (fresher data)
          // This prevents race conditions where stale data overwrites fresh data
          const shouldUpdate = flow.version !== undefined && f.version !== undefined && flow.version > f.version;

          // Also update if versions are equal but slots differ (data might have changed)
          const slotsChanged = JSON.stringify(flow.slots) !== JSON.stringify(f.slots);

          if (shouldUpdate || slotsChanged || flow.version === undefined || f.version === undefined) {
            // Preserve slot content from existing flow when updating
            // Backend may send slots with incomplete content (just IDs), so preserve full content objects
            // Note: durationPlannedMinutes is stored on the flow document, not the eventSlot, so don't preserve it from slot
            let flowWithPreservedContent = {
              ...flow,
              slot:
                flow.slot && f.slot
                  ? {
                      ...f.slot, // Preserve other slot properties (day, id, version)
                      // Use timing from server response, not from old flow
                      startOffsetMinutes: flow.slot.startOffsetMinutes,
                      // Don't preserve durationPlannedMinutes - it's stored on flow, not slot
                    }
                  : flow.slot,
            };
            if (f.slots && flow.slots) {
              flowWithPreservedContent = {
                ...flowWithPreservedContent,
                slots: flow.slots.map((newSlot) => {
                  const existingSlot = f.slots?.find((s) => s.id === newSlot.id);
                  // If existing slot has content (object) and new slot only has ID (string), preserve existing content
                  if (
                    existingSlot &&
                    existingSlot.content &&
                    typeof existingSlot.content === 'object' &&
                    (typeof newSlot.content === 'string' || !newSlot.content)
                  ) {
                    return {
                      ...newSlot,
                      content: existingSlot.content,
                    };
                  }
                  return newSlot;
                }),
              };
            }
            return flowWithPreservedContent;
          }

          return f; // Keep existing flow if it's fresher or same
        });
        useEventStore.getState().setEventFlows(updatedFlows);
      }

      // Route to specific flow handlers (by document id and origin — clients subscribe to master PLAN id)
      const originId =
        typeof flow.origin === 'string'
          ? flow.origin
          : flow.origin && typeof flow.origin === 'object' && 'id' in flow.origin
            ? (flow.origin as { id: string }).id
            : undefined;
      const routeKeys = new Set([flow.id, originId].filter(Boolean) as string[]);

      this.subscriptions.forEach((handlers, subscribedFlowId) => {
        if (!routeKeys.has(subscribedFlowId)) return;
        if (import.meta.env.DEV) {
          console.log(
            `[FLOW-SUB] Routing flowUpdated for ${flow.id} to ${handlers.size} handlers via ${subscribedFlowId}`,
          );
        }
        handlers.forEach((handler) => handler.onFlowUpdated(flow));
      });
    });

    socket.on('flowInvalidated', () => {
      this.subscriptions.forEach((handlers, flowId) => {
        handlers.forEach((handler) => handler.onFlowInvalidated(flowId));
      });
    });
  }
}

// Global instance
const flowSubscriptionManager = new FlowSubscriptionManager();

let contributorSyncQueryClient: QueryClient | null = null;

export function registerContributorSyncQueryClient(queryClient: QueryClient | null) {
  contributorSyncQueryClient = queryClient;
}

function patchContributorFromNotification(notification: ContributorUpdatedNotification) {
  if (!notification?.contributor?.id) {
    return;
  }
  if (contributorSyncQueryClient) {
    patchContributorEverywhere(contributorSyncQueryClient, notification.contributor, notification.eventOriginId);
  }
}

export interface FlowSubscriptionHandler {
  onFlowUpdated: (flow: FlowDto) => void;
  onFlowInvalidated: (flowId: string) => void;
  /** Phase 3: lightweight hint after server commit; prefer refetch / invalidate over full body. */
  onFlowChanged?: (notification: FlowChangedNotification) => void;
  /** Playlist order / show-mode — patch Assets without waiting on GET_FLOW. */
  onMediaPlaylistUpdated?: (notification: MediaPlaylistUpdatedNotification) => void;
  /** Flow host media policy — patch permission icons without waiting on GET_FLOW. */
  onFlowMediaSettingsUpdated?: (notification: FlowMediaSettingsUpdatedNotification) => void;
  /** Media asset approval flip — patch Assets badges without waiting on GET_FLOW. */
  onMediaAssetApprovalUpdated?: (notification: MediaAssetApprovalUpdatedNotification) => void;
}

// Export function to ensure global handlers are initialized
// token is optional - if provided, will ensure socket is connected with auth
export const ensureFlowGlobalHandlers = async (token?: string) => {
  // Ensure socket is connected if token is provided
  if (token && !socket.connected) {
    socket.io.opts.extraHeaders = {
      Authorization: `Bearer ${token}`,
    };
    socket.connect();
  }
  flowSubscriptionManager.ensureGlobalHandlers();
};

export const useFlowSocket = (flowId: string, handler: FlowSubscriptionHandler) => {
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  });
  const lastTokenRef = useRef<string | null>(null);
  const setSocket = useSocketStore((s) => s.setSocket);
  const { getToken } = useAuth();
  const [isConnected, setIsConnected] = useState(socket.connected);
  useEffect(() => {
    // Setup authentication and connection
    (async () => {
      const token = await getToken();
      if (token && token !== lastTokenRef.current) {
        socket.io.opts.extraHeaders = {
          Authorization: `Bearer ${token}`,
        };
        lastTokenRef.current = token;
      }
      if (!socket.connected) {
        socket.connect();
      }
      setSocket(socket);
    })();

    function onConnect() {
      setIsConnected(true);
    }

    function onDisconnect() {
      setIsConnected(false);
    }

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    // Create a stable handler reference that uses the latest handler
    const stableHandler: FlowSubscriptionHandler = {
      onFlowUpdated: (flow) => handlerRef.current.onFlowUpdated(flow),
      onFlowInvalidated: (flowId) => handlerRef.current.onFlowInvalidated(flowId),
      onFlowChanged: (notification) => handlerRef.current.onFlowChanged?.(notification),
      onMediaPlaylistUpdated: (notification) => handlerRef.current.onMediaPlaylistUpdated?.(notification),
      onFlowMediaSettingsUpdated: (notification) => handlerRef.current.onFlowMediaSettingsUpdated?.(notification),
      onMediaAssetApprovalUpdated: (notification) => handlerRef.current.onMediaAssetApprovalUpdated?.(notification),
    };

    flowSubscriptionManager.subscribe(flowId, stableHandler);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      flowSubscriptionManager.unsubscribe(flowId, stableHandler);
    };
  }, [flowId, setSocket, getToken]);

  return { socket, isConnected };
};
